import { useState } from 'react'
import type { JSX } from 'react'

import {
  AlertDialog,
  Button,
  ChannelAvatar,
  Dialog,
  Link,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { usePostingSchedule } from '../../hooks/usePostingSchedule'
import type { PostingScheduleStrategyInput } from '../../hooks/usePostingSchedulePreview'
import { usePostingSchedulePreview } from '../../hooks/usePostingSchedulePreview'
import { PostingTimePreview } from '../PostingTimePreview/PostingTimePreview'

import styles from './PostingTimePreviewModal.module.css'

// TODO(CORE-6589): derive this from the ChannelPage_Channel fragment once
// ChannelContext is migrated; until then it limits the prop to the channel
// fields the preview actually uses.
type PostingTimePreviewChannel = {
  id: string
  service: React.ComponentProps<typeof ChannelAvatar>['channel']
  avatar: string
  name: string
}

interface PostingTimePreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channel: PostingTimePreviewChannel
  strategy: PostingScheduleStrategyInput
  hasQueuedPosts: boolean
  sourceChannelName?: string
}

const CHANNEL_AVATAR_STYLE: React.CSSProperties &
  Record<`--${string}`, string> = {
  '--channel-avatar-border-width': '2px',
}

export const PostingTimePreviewModal = ({
  open,
  onOpenChange,
  channel,
  strategy,
  hasQueuedPosts,
  sourceChannelName,
}: PostingTimePreviewModalProps): JSX.Element => {
  const isMobile = useIsMobileScreenSize()

  const { data: previewData, loading: postingSchedulePreviewLoading } =
    usePostingSchedulePreview({
      channelId: channel.id,
      strategy,
    })

  const { replaceSchedule, loading: replaceScheduleLoading } =
    usePostingSchedule(channel.id)

  const isCopyFromChannel = !!strategy.copyFromChannel

  const [showQueueWarningModal, setShowQueueWarningModal] = useState(false)

  const onClickSaveRecommendations = async (): Promise<void> => {
    if (!previewData?.postingSchedulePreview.proposed) {
      return
    }
    if (hasQueuedPosts) {
      setShowQueueWarningModal(true)
      return
    }
    await replaceScheduleWithPreview()
  }

  const replaceScheduleWithPreview = async (): Promise<void> => {
    setShowQueueWarningModal(false)
    if (!previewData?.postingSchedulePreview.proposed) {
      return
    }
    await replaceSchedule(previewData.postingSchedulePreview.proposed)
    onOpenChange(false)
  }

  return (
    <>
      <AlertDialog
        open={showQueueWarningModal}
        onOpenChange={setShowQueueWarningModal}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Generate New Posting Times</AlertDialog.Title>
          <AlertDialog.Description>
            You already have posts in your Queue. Updating your posting schedule
            will cause adjustments to the timing of these posts. We suggest
            checking your queue after making this change.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button
                variant="secondary"
                onClick={() => setShowQueueWarningModal(false)}
              >
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="primary" onClick={replaceScheduleWithPreview}>
                Confirm
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <Dialog.Content size="xlarge" className={styles.content}>
          <Dialog.CloseButton />
          <Dialog.Header className={styles.header}>
            <div className={styles.avatarWrapper}>
              <ChannelAvatar
                channel={channel.service}
                src={channel.avatar}
                alt={channel.name}
                size="large"
                className={styles.avatar}
                style={CHANNEL_AVATAR_STYLE}
              />
            </div>
            <Dialog.Title
              size={isMobile ? 'medium' : 'large'}
              className={styles.title}
            >
              {isCopyFromChannel && sourceChannelName
                ? `We will copy your schedule from ${sourceChannelName} to ${channel.name}`
                : 'These are your recommended times to post'}
            </Dialog.Title>
            <Dialog.Description className={styles.description}>
              {isCopyFromChannel ? (
                <>
                  Choose the <strong>Next Available</strong> option when
                  scheduling content to use them.
                </>
              ) : (
                <>
                  Customize them, block days and more from channel settings.
                  Reach and engagement depend on more than timing.{' '}
                  <Link
                    href="https://buffer.com/resources/best-time-to-post-social-media/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more
                  </Link>
                </>
              )}
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Body className={styles.body}>
            <PostingTimePreview
              loading={postingSchedulePreviewLoading}
              data={previewData}
            />
          </Dialog.Body>
          <Dialog.Footer showSeparator={false} className={styles.footer}>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              size="large"
              variant="secondary"
              className={styles.action}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="large"
              variant="primary"
              className={styles.action}
              loading={replaceScheduleLoading || postingSchedulePreviewLoading}
              onClick={onClickSaveRecommendations}
            >
              Save Recommendations
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}
